import React, { useCallback } from "react";
import { View, TextInput, StyleSheet, Platform, TouchableOpacity } from "react-native";
import { FontAwesomeIcon, os } from "../platform";
import { faMagnifyingGlass, faClose } from "@fortawesome/free-solid-svg-icons";
import { COLORS } from "../theme/colors";
/**
 * Search Component
 *
 * A customizable search input with optional left and right icons, accessibility support, and style overrides.
 *
 * @param {function} [onTextChange] - Callback when the text input changes
 * @param {function} [onKeyPress] - Callback when a key is pressed in the input
 * @param {function|null} [onLeftIconPress] - Callback for left icon press (if present)
 * @param {function|null} [onRightIconPress] - Callback for right icon press (if present)
 * @param {string} [inputValue] - The value of the text input
 * @param {object} [leftIcon] - Icon definition for the left icon (FontAwesome)
 * @param {object} [rightIcon] - Icon definition for the right icon (FontAwesome)
 * @param {boolean} [showLeftIcon] - Whether to show the left icon
 * @param {boolean} [showRightIcon] - Whether to show the right icon
 * @param {boolean} [autoFocus] - Whether the input should auto-focus on mount
 * @param {object} [iconSizes] - Sizes for left and right icons
 * @param {object} [propStyles] - Style overrides for container, icons, and input
 * @param {string} [accessibilityLabel] - Accessibility label for the search container and input
 * @param {object} [buttonAccessibilityLabels] - Accessibility labels for left and right icon buttons
 * @param {string} [placeholder] - Placeholder text for the input
 * @param {object} [testIDs] - Test IDs for container, icons, and input
 *
 * @returns {JSX.Element}
 */
const Search = ({
  onTextChange = () => {},
  onKeyPress = () => {},
  onLeftIconPress = null,
  onRightIconPress = null,
  inputValue = "",
  leftIcon = faMagnifyingGlass,
  rightIcon = faClose,
  showLeftIcon = false,
  showRightIcon = false,
  autoFocus = false,
  iconSizes = { left: 16, right: 16 },
  propStyles = { container: {}, icon: { left: {}, right: {} }, input: {} },
  accessibilityLabel = "Input",
  buttonAccessibilityLabels = { left: "Left Icon Button", right: "Right Icon Button" },
  placeholder = "Input",
  testIDs = { container: "search-container", leftIcon: "left-icon", rightIcon: "right-icon", input: "text-input" },
}) => {
  /**
   * Renders the left icon (FontAwesome)
   */
  const leftIconComponent = useCallback(() => {
    return (
      <FontAwesomeIcon
        icon={leftIcon}
        size={iconSizes.left}
        style={StyleSheet.flatten([styles.leftIcon, propStyles.icon.left])}
      />
    );
  }, [leftIcon, iconSizes.left, propStyles.icon.left]);

  /**
   * Renders the right icon (FontAwesome)
   */
  const rightIconComponent = useCallback(() => {
    return (
      <FontAwesomeIcon
        icon={rightIcon}
        size={iconSizes.right}
        style={StyleSheet.flatten([styles.rightIcon, propStyles.icon.right])}
      />
    );
  }, [rightIcon, iconSizes.right, propStyles.icon.right]);

  /**
   * Renders the left icon as a touchable button if onLeftIconPress is provided
   */
  const leftTouchableComponent = useCallback(() => {
    return (
      showLeftIcon && (
        <TouchableOpacity
          onPress={onLeftIconPress}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={buttonAccessibilityLabels.left}
          testID={testIDs.leftIcon}
        >
          {leftIconComponent()}
        </TouchableOpacity>
      )
    );
  }, [leftIconComponent, showLeftIcon, onLeftIconPress, buttonAccessibilityLabels.left, testIDs.leftIcon]);

  /**
   * Renders the right icon as a touchable button if onRightIconPress is provided
   */
  const rightTouchableComponent = useCallback(() => {
    return (
      showRightIcon && (
        <TouchableOpacity
          onPress={onRightIconPress}
          accessible={true}
          accessibilityRole="button"
          accessibilityLabel={buttonAccessibilityLabels.right}
          testID={testIDs.rightIcon}
        >
          {rightIconComponent()}
        </TouchableOpacity>
      )
    );
  }, [rightIconComponent, showRightIcon, onRightIconPress, buttonAccessibilityLabels.right, testIDs.rightIcon]);

  /**
   * Handles text input changes
   * @param {string} text
   */
  const handleTextChange = useCallback((text) => onTextChange(text), [onTextChange]);
  /**
   * Handles key press events in the input
   * @param {object} e
   */
  const handleKeyPress = useCallback((e) => onKeyPress(e), [onKeyPress]);

  return (
    <View
      style={StyleSheet.flatten([styles.searchContainer, propStyles.container])}
      accessible={true}
      accessibilityRole="search"
      accessibilityLabel={accessibilityLabel}
      testID={testIDs.container}
    >
      {onLeftIconPress ? leftTouchableComponent() : leftIconComponent()}
      <TextInput
        onChangeText={handleTextChange}
        onKeyPress={handleKeyPress}
        style={StyleSheet.flatten([styles.input, propStyles.input])}
        value={inputValue}
        placeholder={placeholder}
        autoFocus={autoFocus}
        placeholderTextColor={COLORS.tertiary}
        accessibilityLabel={accessibilityLabel}
        testID={testIDs.input}
      />
      {onRightIconPress ? rightTouchableComponent() : rightIconComponent()}
    </View>
  );
};

const styles = StyleSheet.create({
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderColor: COLORS.tertiary,
    backgroundColor: COLORS.background,
    borderWidth: 1,
    borderRadius: 8,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginBottom: 5,
    marginTop: 5,
    marginLeft: 20,
    marginRight: 20,
  },
  rightIcon: {
    marginLeft: 5,
  },
  leftIcon: {
    marginRight: 5,
  },
  input: {
    flex: 1,
    fontSize: 15,
    fontWeight: "500",
    paddingVertical: 6,
    paddingHorizontal: 6,
    outlineStyle: "none",
    outlineWidth: 0,
    outlineColor: "transparent",
    backgroundColor: "transparent",
  },
});

export default Search;
